import { apiClient } from './client'

// Types matching backend schemas (src/schemas/conversation.py)

export interface ConversationSummary {
  conversation_id: number
  title: string | null
  last_message_at: string
  message_count: number
}

export interface ConversationMessage {
  id: number
  user_input: string
  bot_response: string | null
  created_at: string
  feedback_type: 'positive' | 'negative' | null
}

export interface FilterConfig {
  jurisdiction_uuids: string[]
  pub_authority_uuids: string[]
  document_legislation_uuids: string[]
}

export interface ConversationDetail {
  conversation_id: number
  title: string | null
  messages: ConversationMessage[]
  created_at: string
  filter_config: FilterConfig | null
}

export interface ConversationDeleteResponse {
  conversation_id: number
  deleted: boolean
  message: string
}

export interface ConversationTitleResponse {
  conversation_id: number
  title: string
}

/**
 * Fetch all conversations for the current user.
 * GET /api/v1/conversations/
 */
export async function getConversations(
  limit: number = 50,
  offset: number = 0,
): Promise<ConversationSummary[]> {
  const response = await apiClient.get<ConversationSummary[]>(
    '/api/v1/conversations/',
    { params: { limit, offset } },
  )
  return response.data
}

/**
 * Fetch full conversation detail including messages.
 * GET /api/v1/conversations/{id}
 */
export async function getConversation(
  conversationId: number,
): Promise<ConversationDetail> {
  const response = await apiClient.get<ConversationDetail>(
    `/api/v1/conversations/${conversationId}`,
  )
  return response.data
}

/**
 * Delete a conversation and all its messages.
 * DELETE /api/v1/conversations/{id}
 */
export async function deleteConversation(
  conversationId: number,
): Promise<ConversationDeleteResponse> {
  const response = await apiClient.delete<ConversationDeleteResponse>(
    `/api/v1/conversations/${conversationId}`,
  )
  return response.data
}

/**
 * Rename a conversation's title.
 * PATCH /api/v1/conversations/{id}/title
 */
export async function renameConversation(
  conversationId: number,
  title: string,
): Promise<ConversationTitleResponse> {
  const response = await apiClient.patch<ConversationTitleResponse>(
    `/api/v1/conversations/${conversationId}/title`,
    { title },
  )
  return response.data
}
